'use client';

import { ChevronDown } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';
import { trackEvent } from '@/utils/analytics';
import { cn } from '@/utils/utils';
import type { DropdownItem } from './types';

interface MobileDropdownProps {
  title: string;
  items: DropdownItem[];
  allItemsHref: string;
  allItemsLabel?: string;
  onClose: () => void;
  trackingCategory?: string;
}

export function MobileDropdown({
  title,
  items,
  allItemsHref,
  allItemsLabel = `All ${title}`,
  onClose,
  trackingCategory,
}: MobileDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);

    trackEvent({
      action: newState ? 'mobile_dropdown_open' : 'mobile_dropdown_close',
      category: trackingCategory || title.toLowerCase(),
      label: title,
      value: 1,
    });
  };

  const handleItemClick = (itemTitle: string) => {
    trackEvent({
      action: 'mobile_dropdown_item_click',
      category: trackingCategory || title.toLowerCase(),
      label: itemTitle,
      value: 1,
    });
    onClose();
  };

  const handleAllItemsClick = () => {
    trackEvent({
      action: 'mobile_all_items_click',
      category: trackingCategory || title.toLowerCase(),
      label: allItemsLabel,
      value: 1,
    });
    onClose();
  };

  return (
    <div className="border-b border-gray-200">
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center justify-between w-full px-4 py-3 text-left"
        aria-expanded={isOpen}
      >
        <span className="text-lg font-medium">{title}</span>
        <ChevronDown
          className={cn(
            'h-5 w-5 transition-transform duration-200',
            isOpen ? 'rotate-180' : '',
          )}
          aria-hidden="true"
        />
      </button>
      <section
        className={cn(
          'overflow-hidden transition-all duration-300 ease-in-out',
          isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0',
        )}
        aria-labelledby={`${title.toLowerCase()}-button`}
      >
        <div className="py-2 space-y-2">
          <Link
            href={allItemsHref}
            className="block px-8 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg"
            onClick={handleAllItemsClick}
          >
            {allItemsLabel}
          </Link>
          {items.map((item) => (
            <Link
              key={item.title}
              href={item.href}
              className="block px-8 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg"
              onClick={() => handleItemClick(item.title)}
            >
              {item.title}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
